import { buildNamespace, useOpportunityReviewGroupState } from '@utils/hook/userPreferences/UseGroupState';
import {
  GroupContext,
  OPPORTUNITY_REVIEW_BOARD_TABLE,
  Preference,
  PreferenceResponseStatus,
  UNKNOWN_ERROR_MESSAGE,
} from '@utils/userPreferencesUtils/Types';
import { useOpportunityReviewBoardGroupEventState } from '../utils/OpportunityReviewBoardGroupContext';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { authentication, useAnalytics } from '@ecosystem/newron-client-lib';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import { PageName, PageType } from '@utils/pageConfig/Types';
import {
  groupElementClickedTrackingFunction,
  OPPORTUNITY_REVIEW_TABLE_ELEMENT_CLICKED_TITLE,
  TableElementClickedParam,
} from '@utils/page-analytics/model/GroupElementClickTracking';
import { actions, overlays } from '@ecosystem/newron-design-system';

export interface Props {
  context: GroupContext;
}

export const DeleteOpportunityReviewBoardModalGroup: React.FC<Props> = ({ context }) => {
  const { deleteOpportunityReviewGroup, getOpportunityReviewGroup, deletePreferencePermissionOpportunityReviewGroup } =
    useOpportunityReviewGroupState(context);
  const {
    groupNameToBeDelete,
    setGroupNameToBeDelete,
    isDeleteModalGroupPopUp,
    setIsDeleteModalGroupPopUp,
    currentSelectedFeature,
  } = useOpportunityReviewBoardGroupEventState();
  const [error, setError] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const pagesConfig = usePageConfig();
  const { track } = useAnalytics();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);

  const handleCancelDelete = () => {
    setError('');
    setIsDeleting(false);
    setIsDeleteModalGroupPopUp(false);
    setGroupNameToBeDelete('');
  };
  const getSpecificQuery = (type: 'delete' | 'get' | 'deletePermission') => {
    const perferenceQueryMap = {
      delete: deleteOpportunityReviewGroup,
      get: getOpportunityReviewGroup,
      deletePermission: deletePreferencePermissionOpportunityReviewGroup,
    };

    return perferenceQueryMap[type];
  };

  const constructDeleteGroupQuery = () => {
    const namespace = buildNamespace(context);
    const userId = authentication.getUser()?.userId ?? '';

    const deleteGroupQuery = getSpecificQuery('delete');
    const getGroupQuery = getSpecificQuery('get');
    const deletePermissionQuery = getSpecificQuery('deletePermission');

    // Retrieve current preference
    const data = getGroupQuery.data as Preference<any>[];
    const currentPreference = data.find(
      (preference) => preference.namespace === namespace && preference.key === groupNameToBeDelete,
    );

    // Reusable mutation handler
    const handleMutation = (query: any, variables: any, onSuccessCallback: (response: any) => void) => {
      query.mutate(variables, {
        onSuccess: (response: { status: PreferenceResponseStatus; message: React.SetStateAction<string> }) => {
          setIsDeleting(false);
          if (response.status === PreferenceResponseStatus.SUCCESS) {
            onSuccessCallback(response);
          } else {
            setError(response.message);
          }
        },
        onError: () => {
          setIsDeleting(false);
          setError(UNKNOWN_ERROR_MESSAGE);
        },
      });
    };

    // If the user is the owner, delete the group
    if (currentPreference?.isPreferenceOwner) {
      handleMutation(deleteGroupQuery, { key: groupNameToBeDelete }, () => handleCancelDelete());
      return;
    }

    // Delete preference permission regardless of ownership
    handleMutation(deletePermissionQuery, { key: groupNameToBeDelete, userId }, () => handleCancelDelete());
  };

  const handleDeleteGroup = () => {
    //Amplitude Tracking when Delete group button is click
    const trackingParam: TableElementClickedParam = {
      eventTitle: OPPORTUNITY_REVIEW_TABLE_ELEMENT_CLICKED_TITLE,
      elementName: `Delete ${currentSelectedFeature.feature} Group`,
      elementType: `Table ${currentSelectedFeature.feature} Group`,
      track: track,
      optionChosen: groupNameToBeDelete,
      pageName: PageName.OPPORTUNITY_REVIEW,
      pageType: PageType.OPPORTUNITY_REVIEW,
      sideFilters: sideFiltersState.appliedFilters,
      access: pagesConfig.access,
      tableName: OPPORTUNITY_REVIEW_BOARD_TABLE,
    };

    groupElementClickedTrackingFunction(trackingParam);

    setError('');
    setIsDeleting(true);

    constructDeleteGroupQuery();
  };
  const { Modal } = overlays;

  const { Button } = actions;
  return (
    <Modal isOpen={isDeleteModalGroupPopUp} onClose={handleCancelDelete}>
      <Modal.Header>
        <h2>Delete Opportunity Review Group</h2>
      </Modal.Header>
      <Modal.Body>
        <p>Are you sure you want to delete “{groupNameToBeDelete}” group?</p>
        {error && <span className="Input__error">{error}</span>}
      </Modal.Body>
      <Modal.Footer>
        <Button mode="secondary" disabled={isDeleting} onClick={handleCancelDelete}>
          Cancel
        </Button>
        <Button onClick={handleDeleteGroup} disabled={isDeleting}>
          {isDeleting ? 'Deleting...' : 'Yes, delete group'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
